import { Link } from "react-router-dom";

import styled from "styled-components";

import { Typography } from "antd";

import { CmtActiveField } from "@app/components/users/field/CmtActiveField.tsx";
import { CmtBenchField } from "@app/components/users/field/CmtBenchField.tsx";
import { CmtDepartmentField } from "@app/components/users/field/CmtDepartmentField.tsx";
import { CmtUserAvatar } from "@app/components/users/field/CmtUserAvatar.tsx";
import { User } from "@app/models/User.ts";
import { capitalizeFirstLetter } from "@app/services/utils/userUtils.ts";
import { colors } from "@app/theme.ts";

const StyledCard = styled(Link)<{ $inactive: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  height: 100%;
  padding: 2.5rem 0.75rem 1.25rem;
  border: 1px solid ${colors.border};
  border-radius: 16px;
  background: ${colors.white};
  color: inherit;
  text-align: center;
  opacity: ${({ $inactive }) => ($inactive ? 0.75 : 1)};
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    border-color 0.15s ease,
    opacity 0.15s ease;

  &:hover,
  &:focus-visible {
    color: inherit;
    opacity: 1;
    transform: translateY(-3px);
    border-color: ${colors.pink};
    box-shadow: 0 12px 28px -12px rgba(16, 16, 43, 0.25);
  }

  &:focus-visible {
    outline: 2px solid ${colors.pink};
    outline-offset: 2px;
  }
`;

const StyledName = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
`;

const StyledCorner = styled.div<{ $side: "left" | "right" }>`
  position: absolute;
  top: 0.625rem;
  ${({ $side }) => $side}: 0.625rem;

  .ant-tag {
    margin-inline-end: 0;
  }
`;

const StyledTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.375rem;
  margin-top: auto;

  .ant-tag {
    margin-inline-end: 0;
  }
`;

interface CmtUserCardProps {
  user: User;
}

export const CmtUserCard = ({ user }: CmtUserCardProps) => (
  <StyledCard to={`/users/${user.id}`} $inactive={!user.isActive}>
    {user.isOnBench && (
      <StyledCorner $side="left">
        <CmtBenchField />
      </StyledCorner>
    )}
    <StyledCorner $side="right">
      <CmtActiveField isActive={user.isActive} />
    </StyledCorner>
    <CmtUserAvatar user={user} size={72} />
    <div>
      <StyledName>
        {capitalizeFirstLetter(user.firstName)} {user.lastName.toUpperCase()}
      </StyledName>
      {user.userJob?.name && (
        <Typography.Text type="secondary" style={{ fontSize: "0.85rem" }}>
          {user.userJob.name}
        </Typography.Text>
      )}
    </div>
    <StyledTags>
      <CmtDepartmentField department={user.department} />
    </StyledTags>
  </StyledCard>
);
